import { Box, NativeSelect, Text } from '@chakra-ui/react'
import React from 'react'

import {
  getNodeSelectFieldStyles,
  nodeSelectIndicatorStyles,
} from './nodeInputStyles'
import { NodeLabel } from './NodeLabel'

export interface NodeSelectProps extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'size' | 'children' | 'onMouseDown' | 'onClick'
> {
  label: string
  required?: boolean
  error?: string
  children: React.ReactNode
  /** Disables the select and its indicator */
  disabled?: boolean
  onMouseDown?: (e: React.MouseEvent<HTMLSelectElement>) => void
  onClick?: (e: React.MouseEvent<HTMLSelectElement>) => void
}

/**
 * Standardized select component for switchboard nodes with floating label.
 * Label appears on the border to save space.
 * Automatically applies the 'nodrag' class to prevent React Flow drag events.
 * Pass `disabled` to disable the select (routes to NativeSelect.Root).
 */
export const NodeSelect = React.forwardRef<HTMLSelectElement, NodeSelectProps>(
  (
    {
      label,
      required = false,
      error,
      children,
      onMouseDown,
      onClick,
      disabled,
      ...selectProps
    },
    ref,
  ) => {
    const handleMouseDown = (e: React.MouseEvent<HTMLSelectElement>) => {
      onMouseDown?.(e)
      e.stopPropagation()
    }

    const handleClick = (e: React.MouseEvent<HTMLSelectElement>) => {
      onClick?.(e)
      e.stopPropagation()
    }

    const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
      selectProps.onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
      selectProps.onBlur?.(e)
    }

    return (
      <Box position="relative" width="100%">
        <NativeSelect.Root disabled={disabled}>
          <NativeSelect.Field
            ref={ref}
            className="nodrag"
            {...selectProps}
            {...getNodeSelectFieldStyles(!!error)}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            {children}
          </NativeSelect.Field>
          <NativeSelect.Indicator {...nodeSelectIndicatorStyles} />
        </NativeSelect.Root>
        <NodeLabel label={label} required={required} />
        {error && (
          <Text fontSize="xs" color="fg.error" mt={1}>
            {error}
          </Text>
        )}
      </Box>
    )
  },
)

NodeSelect.displayName = 'NodeSelect'
